import React from 'react'
import clsx from 'clsx'
import { merge } from 'lodash'
import { Avatar } from '@mui/material'
import { useAnimate } from 'framer-motion'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'

import { StyledBadge } from '../../../molecules/UserInfo/styles'

import { getInitials } from '../../../../utilities'
import { selectMediaCache, finishAnimation } from '@SonarCX/slices/mediaCache'
import useAvatarLoading from '../useAvatarLoading'
import useStyles from './styles'

const Wrapper = React.memo(
  ({
    showBadge,
    activity,
    activeStatus,
    badgeClass,
    endPosition,
    children
  }) => {
    const classes = useStyles({ onlineStatus: activity ?? activeStatus })

    if (!showBadge) {
      return (
        <span className={clsx(endPosition && classes.endPosition)}>
          {children}
        </span>
      )
    }

    return (
      <StyledBadge
        overlap='circular'
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        variant='dot'
        className={clsx(classes.root, badgeClass, classes.badge)}
      >
        {children}
      </StyledBadge>
    )
  }
)

const CustomAvatar = ({
  user,
  maxInitials = 2,
  intersect,
  showBadge,
  activity,
  group,
  badgeClass,
  endPosition,
  disableAnimation = false,
  ...props
}) => {
  const [scope, animate] = useAnimate()
  const dispatch = useAppDispatch()

  const { finishedAnimations } = useAppSelector(selectMediaCache)

  const { src, elem, activeStatus } = useAvatarLoading({
    user,
    intersect,
    group
  })

  const userID = React.useMemo(() => user?.id ?? '', [user])
  const [doneAnimation, setDoneAnimation] = React.useState(
    finishedAnimations.includes(userID)
  )

  const fallbackName = React.useMemo(
    () => getInitials(user?.name, maxInitials),
    [user, maxInitials]
  )
  const shouldShowOverlay = React.useMemo(() => {
    if (doneAnimation) {
      return false
    }

    if (finishedAnimations.includes(userID)) {
      return false
    }

    return true
  }, [doneAnimation, finishedAnimations, userID])

  // Hide grey circle when image is loaded, only needs to run once
  React.useLayoutEffect(() => {
    if (!scope.current || doneAnimation) {
      return
    }

    if (!src) {
      scope.current.style.opacity = 1
      return
    }

    if (disableAnimation) {
      setDoneAnimation(true)
      scope.current.style.opacity = 0
      return
    }

    const timer = setTimeout(() => {
      animate(
        scope.current,
        {
          opacity: 0
        },
        {
          duration: 1,
          onComplete: () => {
            setDoneAnimation(true)
            dispatch(finishAnimation(userID))
          }
        }
      )
    }, 250)

    return () => {
      clearTimeout(timer)
    }
  }, [scope, doneAnimation, src, disableAnimation, animate, dispatch, userID])

  return (
    <Wrapper
      showBadge={showBadge}
      activity={activity}
      activeStatus={activeStatus}
      badgeClass={badgeClass}
      endPosition={endPosition}
    >
      <div
        style={{
          borderRadius: '50%'
        }}
      >
        <div
          style={{
            position: 'relative',
            borderRadius: '50%'
          }}
        >
          <Avatar src={src} {...props}>
            {elem ?? fallbackName}
          </Avatar>

          {shouldShowOverlay && (
            <Avatar
              ref={scope}
              {...props}
              style={merge(
                {
                  top: '0',
                  left: '0'
                },
                props.style ?? {},
                { position: 'absolute' }
              )}
            >
              {fallbackName}
            </Avatar>
          )}
        </div>
      </div>
    </Wrapper>
  )
}

export default React.memo(CustomAvatar)
